// ============================================================================
// PRINTING SERVICE (ADR-0062) — voucher PDFs, mail drafts and Settings → Print Layouts.
// ============================================================================
// Binary outputs come back as blobs; the browser opens, downloads or shares them.

import { get, post, put, del } from '../client/axiosClient';
import { appendRepeatedQuery, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import { salesInvoicePrintEndpoints, printLayoutEndpoints } from '../endpoints/printing.endpoints';
import type {
  ApiResponse, ID, PrintOptionsDto, OutputProfileDto, SaveOutputProfileRequest,
  DocumentEmailTemplateDto, SaveDocumentEmailTemplateRequest, OrgContactDto, SaveOrgContactRequest, StateOptionDto,
} from '../types';

/** A file returned by the API, with the name from its Content-Disposition. */
export interface PrintedFile { blob: Blob; fileName: string }

const fileNameFrom = (headers: Record<string, unknown> | undefined, fallback: string): string => {
  const disposition = (headers?.['content-disposition'] ?? headers?.['Content-Disposition']) as string | undefined;
  const match = disposition && /filename\*=UTF-8''([^;]+)|filename="?([^;"]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1] || match[2]) : fallback;
};

class PrintingService {
  // --- Sales Invoice outputs -------------------------------------------------

  async getSalesInvoiceOptions(id: ID): Promise<PrintOptionsDto> {
    const r = await get<ApiResponse<PrintOptionsDto>>(salesInvoicePrintEndpoints.options(id));
    return r.data.data;
  }

  /** The PDF. `inline` = to print in the browser (logged PRINT); otherwise a download (logged PDF). */
  async getSalesInvoicePdf(id: ID, copies: string[], inline: boolean): Promise<PrintedFile> {
    const url = appendRepeatedQuery(buildEndpointWithQuery(salesInvoicePrintEndpoints.pdf(id), { inline }), 'copies', copies);
    const r = await get<Blob>(url, { responseType: 'blob' });
    return { blob: r.data, fileName: fileNameFrom(r.headers as any, 'invoice.pdf') };
  }

  /** The .eml draft (X-Unsent) with the PDF attached. */
  async getSalesInvoiceMailDraft(id: ID, to: string, cc: string, copies: string[]): Promise<PrintedFile> {
    const url = appendRepeatedQuery(buildEndpointWithQuery(salesInvoicePrintEndpoints.mailDraft(id), { to, cc }), 'copies', copies);
    const r = await get<Blob>(url, { responseType: 'blob' });
    return { blob: r.data, fileName: fileNameFrom(r.headers as any, 'invoice.eml') };
  }

  /** Record an output the browser made itself (Web Share). */
  async logSalesInvoiceShare(id: ID, copies: string[]): Promise<void> {
    await post(salesInvoicePrintEndpoints.log(id), { outputKind: 'SHARE', copies });
  }

  // --- Print Layouts settings ---------------------------------------------------

  async getProfile(documentType: string): Promise<OutputProfileDto> {
    const r = await get<ApiResponse<OutputProfileDto>>(printLayoutEndpoints.profile(documentType));
    return r.data.data;
  }

  async saveProfile(documentType: string, data: SaveOutputProfileRequest): Promise<OutputProfileDto> {
    const r = await put<ApiResponse<OutputProfileDto>>(printLayoutEndpoints.profile(documentType), data);
    return r.data.data;
  }

  async resetProfile(documentType: string, rowVersion: number): Promise<OutputProfileDto> {
    const r = await del<ApiResponse<OutputProfileDto>>(buildEndpointWithQuery(printLayoutEndpoints.profile(documentType), { rowVersion }));
    return r.data.data;
  }

  async copyProfile(documentType: string, fromEntityId: string): Promise<OutputProfileDto> {
    const r = await post<ApiResponse<OutputProfileDto>>(buildEndpointWithQuery(printLayoutEndpoints.copy(documentType), { fromEntityId }));
    return r.data.data;
  }

  async uploadImage(documentType: string, kind: 'logo' | 'signature', file: File): Promise<OutputProfileDto> {
    const form = new FormData();
    form.append('file', file);
    const r = await post<ApiResponse<OutputProfileDto>>(printLayoutEndpoints.image(documentType, kind), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data.data;
  }

  async removeImage(documentType: string, kind: 'logo' | 'signature'): Promise<OutputProfileDto> {
    const r = await del<ApiResponse<OutputProfileDto>>(printLayoutEndpoints.image(documentType, kind));
    return r.data.data;
  }

  /** The saved image as a blob, or null when none is saved. */
  async getImage(documentType: string, kind: 'logo' | 'signature'): Promise<Blob | null> {
    try {
      const r = await get<Blob>(printLayoutEndpoints.image(documentType, kind), { responseType: 'blob' });
      return r.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  /** A sample document rendered with the unsaved settings. */
  async preview(documentType: string, draft: SaveOutputProfileRequest): Promise<Blob> {
    const r = await post<Blob>(printLayoutEndpoints.preview(documentType), draft, { responseType: 'blob' });
    return r.data;
  }

  async getEmailTemplate(documentType: string): Promise<DocumentEmailTemplateDto> {
    const r = await get<ApiResponse<DocumentEmailTemplateDto>>(printLayoutEndpoints.emailTemplate(documentType));
    return r.data.data;
  }

  async saveEmailTemplate(documentType: string, data: SaveDocumentEmailTemplateRequest): Promise<DocumentEmailTemplateDto> {
    const r = await put<ApiResponse<DocumentEmailTemplateDto>>(printLayoutEndpoints.emailTemplate(documentType), data);
    return r.data.data;
  }

  async getContacts(): Promise<OrgContactDto[]> {
    const r = await get<ApiResponse<OrgContactDto[]>>(printLayoutEndpoints.contacts());
    return r.data.data;
  }

  async getStates(countryId: string): Promise<StateOptionDto[]> {
    const r = await get<ApiResponse<StateOptionDto[]>>(buildEndpointWithQuery(printLayoutEndpoints.states(), { countryId }));
    return r.data.data ?? [];
  }

  async saveContact(data: SaveOrgContactRequest): Promise<OrgContactDto> {
    const r = await put<ApiResponse<OrgContactDto>>(printLayoutEndpoints.contacts(), data);
    return r.data.data;
  }
}

export const printingService = new PrintingService();
export default printingService;
